import React from 'react';
import PropTypes from 'prop-types';

// Hooks.
import { useTranslation } from 'react-i18next';
import useCheckPermissionGroups from 'app/client/utilities/hooks/useCheckPermissionGroups';
import useIsOwner from 'app/client/utilities/hooks/useIsOwner';

// Styled.
import * as S from './styled';

/**
 * Only show the children if the logged in user has the given (to) permissions.
 *
 * @param {*} children The children to render if the user has access.
 * @param {string|string[]|boolean} to The required permissions.
 * @param {string|string[]|boolean} forRole The required roles
 * @param {boolean} isOwner The logged in user must be the owner of the customer.
 * @param {string} message The (optional) message to show if the user has no permission.
 * @param {boolean} noError If true then null is returned.
 *
 * @returns {Element} The rendered component.
 */
const Restricted = ({
  children,
  to = false,
  forRole = false,
  isOwner = false,
  message = false,
  noError = false,
}) => {
  // Translations.
  const { t } = useTranslation();

  // Permissions.
  const hasPermission = useCheckPermissionGroups(to, forRole);
  const allowed = !to || hasPermission;

  // Owner check.
  const owner = useIsOwner();

  if (
    // If the user has access, or this is not production and the user is a developer, allow it.
    allowed &&
    // Check if the user is a owner of the customer.
    (!isOwner || (isOwner && owner))
  ) {
    return children;
  }

  if (message !== false) {
    return message;
  }

  if (noError) {
    return null;
  }

  return (
    <S.Wrapper>
      <S.Title>{t('core:error.permission.title')}</S.Title>
      <p>{t('core:error.permission.message')}</p>
      <S.Nope />
    </S.Wrapper>
  );
};

Restricted.propTypes = {
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string), PropTypes.bool]),
  forRole: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.bool,
  ]),
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.bool]),
  isOwner: PropTypes.bool,
  noError: PropTypes.bool,
  children: PropTypes.any,
};

export default Restricted;
